import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Leaf, Award, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useStore } from '../admin/context/ProductStore';
import './Home.css';

const features = [
  { icon: <Leaf size={26} />, title: 'Farm to Home', desc: 'Directly sourced. No middlemen, fresher quality guaranteed.' },
  { icon: <Shield size={26} />, title: '100% Natural', desc: 'Zero artificial flavours, preservatives, or added sugars.' },
  { icon: <Truck size={26} />, title: 'Fast Delivery', desc: 'Free shipping on orders above ₹599. Pan-India.' },
  { icon: <Award size={26} />, title: 'Lab Tested', desc: 'Every batch tested in certified labs before dispatch.' },
];

export default function Home({ onAddToCart }) {
  const { products = [], categories = [], homepage, loading, getFullUrl, featuredProductIds = [] } = useStore();
  const carouselRef = useRef(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Only show published products on storefront
  const publishedProducts = products.filter(p => p.status === 'published');

  // Admin-selected featured products; fallback to top 8 by rating
  const featuredProducts = featuredProductIds.length > 0
    ? featuredProductIds
        .map(id => publishedProducts.find(p => String(p.id) === String(id) || p._id === id))
        .filter(Boolean)
    : [...publishedProducts].slice(0, 8);

  const CARDS_VISIBLE = 4; // cards shown at once
  const maxIndex = Math.max(0, featuredProducts.length - CARDS_VISIBLE);

  const scrollTo = (idx) => {
    const clamped = Math.max(0, Math.min(idx, maxIndex));
    setCarouselIndex(clamped);
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.offsetWidth / CARDS_VISIBLE;
      carouselRef.current.scrollTo({ left: clamped * cardWidth, behavior: 'smooth' });
    }
  };

  // Auto-advance every 4s
  useEffect(() => {
    if (featuredProducts.length <= CARDS_VISIBLE) return;
    const t = setInterval(() => {
      setCarouselIndex(prev => {
        const next = prev >= maxIndex ? 0 : prev + 1;
        if (carouselRef.current) {
          const cardWidth = carouselRef.current.offsetWidth / CARDS_VISIBLE;
          carouselRef.current.scrollTo({ left: next * cardWidth, behavior: 'smooth' });
        }
        return next;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [featuredProducts.length, maxIndex]);

  // Load Elfsight Script for widgets
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Optional: remove script on unmount, or leave it globally
    };
  }, []);

  return (
    <main className="home">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg">
          {homepage?.hero?.bg && (
            <img
              src={getFullUrl(homepage.hero.bg)}
              alt="Premium Dry Fruits"
              className="hero-bg-img"
              style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.6s ease' }}
            />
          )}
          <div className="hero-overlay" />
          {/* Decorative blobs */}
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
        </div>

        <div className="container hero-inner">
          {/* Content removed to show full banner image */}
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div className="marquee-strip">
        <div className="marquee-track">
          {[...Array(2)].flatMap(() =>
            ['🌿 No Preservatives', '🏆 Farm to Table', '✅ 100% Natural', '🚚 Free Delivery ₹599+', '🔬 Lab Tested', '⭐ Premium Quality', '🍯 No Added Sugar', '🌾 Sourced Direct'].map((txt, i) => (
              <span key={`${txt}-${i}`} className="marquee-item">{txt}</span>
            ))
          )}
        </div>
      </div>

      {/* ── CATEGORIES ── */}
      <section className="section container">
        <div className="section-header">
          <span className="eyebrow">Browse Our Range</span>
          <h2 className="section-title">Shop by Category</h2>
          <p className="section-sub">50+ products across {categories.length} categories — all 100% natural</p>
        </div>
        <div className="categories-grid">
          {categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/shop?cat=${cat.slug}`}
              className="category-card"
              style={{ animationDelay: `${i * 0.07}s` }}
            >
              <div className="cat-img-wrap">
                <img 
                  src={getFullUrl(cat.image)} 
                  alt={cat.name} 
                  className="cat-img"
                  style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.4s ease' }}
                  onError={e => { e.target.style.display = 'none'; }} 
                />
                <div className="cat-emoji-overlay">{cat.emoji}</div>
              </div>
              <div className="cat-body">
                <p className="cat-name">{cat.name}</p>
                <p className="cat-count">{cat.count} products</p>
              </div>
              <div className="cat-arrow">→</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── WHY KOA ── */}
      <section className="why-section">
        <div className="container why-inner">
          <div className="why-left">
            <span className="eyebrow" style={{ color: '#fff', opacity: 0.7 }}>Why Choose Us</span>
            <h2 className="why-title">The KOA<br />Difference</h2>
            <p className="why-desc">We're not just another dry fruits company. We're a movement to make healthy eating delicious, affordable, and accessible to every Indian household.</p>
            <Link to="/about" className="btn-yellow">Learn More <ArrowRight size={16} /></Link>
          </div>
          <div className="why-right">
            {features.map((f, i) => (
              <div key={i} className="why-card">
                <div className="why-icon">{f.icon}</div>
                <div>
                  <h3 className="why-card-title">{f.title}</h3>
                  <p className="why-card-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MOST LOVED PRODUCTS — CAROUSEL ── */}
      <section className="section products-section">
        <div className="container">
          <div className="products-header">
            <div>
              <span className="eyebrow">Our Bestsellers</span>
              <h2 className="section-title">Most Loved Products</h2>
            </div>
            <Link to="/shop" className="btn-outline view-all-btn">
              View All <ArrowRight size={15} />
            </Link>
          </div>

          {/* Carousel wrapper */}
          <div className="carousel-outer">
            <button
              className="carousel-arrow carousel-arrow-left"
              onClick={() => scrollTo(carouselIndex - 1)}
              disabled={carouselIndex === 0}
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>

            <div className="carousel-track" ref={carouselRef}>
              {featuredProducts.map(p => (
                <div key={p.id || p._id} className="carousel-card-slot">
                  <ProductCard product={p} onAddToCart={onAddToCart} />
                </div>
              ))}
            </div>

            <button
              className="carousel-arrow carousel-arrow-right"
              onClick={() => scrollTo(carouselIndex + 1)}
              disabled={carouselIndex >= maxIndex}
              aria-label="Next"
            >
              <ChevronRightIcon size={22} />
            </button>
          </div>

          {/* Dot indicators */}
          {maxIndex > 0 && (
            <div className="carousel-dots">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  className={`carousel-dot ${carouselIndex === i ? 'active' : ''}`}
                  onClick={() => scrollTo(i)}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── VIDEO SECTION ── */}
      <section className="video-section-full">
        <div className="video-aspect-ratio">
          {homepage.video ? (
            <video 
              className="home-promo-video"
              muted
              autoPlay
              loop
              playsInline
              key={homepage.video} // Force reload on change
            >
              <source src={getFullUrl(homepage.video)} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="video-placeholder-overlay">
              <div className="play-button-icon">▶</div>
              <p>Video will be added here</p>
            </div>
          )}
        </div>
      </section>


      <section className="promo-section">
        <div className="container promo-grid">
          {/* Gift box promo */}
          <div className="promo-card promo-red">
            <div className="promo-card-content">
              <span className="promo-eyebrow">Limited Time</span>
              <h3 className="promo-card-title">Gift the Goodness<br />of Nature</h3>
              <p className="promo-card-desc">Curated gift boxes for every occasion. Free greeting card included.</p>
              <Link to="/shop?cat=gift-boxes" className="btn-yellow">Shop Gift Boxes <ArrowRight size={15} /></Link>
            </div>
            <img
              src={getFullUrl(homepage?.promos?.red)}
              alt="Gift Box"
              className="promo-img"
              style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.6s ease' }}
            />
          </div>
          {/* Trail mix promo */}
          <div className="promo-card promo-yellow">
            <div className="promo-card-content">
              <span className="promo-eyebrow" style={{ color: 'var(--red)' }}>New Arrival</span>
              <h3 className="promo-card-title" style={{ color: 'var(--dark)' }}>Trail Mix<br />Deluxe Blend</h3>
              <p className="promo-card-desc" style={{ color: 'var(--dark-3)' }}>9 premium ingredients. Perfect gym partner.</p>
              <Link to="/product/trail-mix-deluxe" className="btn-primary">Try Now <ArrowRight size={15} /></Link>
            </div>
            <img
              src={getFullUrl(homepage?.promos?.yellow)}
              alt="Trail Mix"
              className="promo-img"
              style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.6s ease' }}
            />
          </div>
        </div>
      </section>




      {/* ── GOOGLE REVIEWS (Elfsight) ── */}
      <section className="section testimonials-section" style={{ paddingBottom: 0 }}>
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '40px' }}>
            <span className="eyebrow">Customer Love</span>
            <h2 className="section-title">What Our Customers Say</h2>
          </div>
          {/* Elfsight Google Reviews | Untitled Google Reviews */}
          <div className="elfsight-app-e4ce29c8-8376-4149-9801-a594e95645b6" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* ── INSTAGRAM FEED (Elfsight) ── */}
      <section className="section blog-home-section">
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span className="eyebrow">Follow Our Journey</span>
            <h2 className="section-title">Follow Us on Instagram</h2>
          </div>
          {/* Elfsight Instagram Feed | Untitled Instagram Feed */}
          <div className="elfsight-app-72040597-3374-4ef8-8621-6aa605fd2107" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section className="newsletter-section">
        <div className="container newsletter-inner">
          <div className="newsletter-left">
            <span className="eyebrow" style={{ color: 'var(--yellow)' }}>Stay Updated</span>
            <h2 className="newsletter-title">Get Exclusive Offers<br />& Health Tips</h2>
            <p className="newsletter-sub">Join 50,000+ subscribers. No spam, ever.</p>
          </div>
          <form className="newsletter-form" onSubmit={e => e.preventDefault()}>
            <input type="email" placeholder="Enter your email address" className="newsletter-input" required />
            <button type="submit" className="btn-yellow newsletter-btn">Subscribe →</button>
          </form>
        </div>
      </section>

    </main>
  );
}
